import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { required } from 'components/helpers/validators';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/store';
import { Option } from 'shared/types/general';
import { IContract } from '../../types';
import {
  IdentityField,
  CheckboxAdapter,
  TextFieldAdapter,
  DatePickerAdapter,
  AutocompleteAdapter,
  MultipleAutocompleteAdapter
} from 'components/enhanced';

interface ContractHeaderFormParams {
  contract: IContract;
  onSubmit: any;
  suppliers: Option[];
  laboratories: Option[];
  disableEdit?: boolean;
}

function ContractHeaderForm({
  contract,
  onSubmit,
  suppliers,
  laboratories,
  disableEdit
}: ContractHeaderFormParams) {
  const { t } = useTranslation();
  const sap_active = useAppSelector((state) => state.auth.sap_active);

  return (
    <div className={'pt-4'}>
      <EnhancedForm
        title={t('global.base-info-form')}
        onSubmit={disableEdit ? {} : onSubmit}
        hideControls={disableEdit}
        disableEdit={disableEdit}
        formId="contractForm"
        autoStyleLayout={true}
        layout="grid"
        columns={12}
        gap={2.5}>
        {sap_active && (
          <IdentityField
            name="sync_with_sap"
            initialValue={contract.sync_with_sap}
            component={CheckboxAdapter}
            label={t('entities.sap.sync_with_sap')}
            placeholder={t('entities.sap.sync_with_sap')}
            type="checkbox"
          />
        )}
        <IdentityField
          gridSpan={6}
          name="code"
          initialValue={contract.code}
          component={TextFieldAdapter}
          label={t('global.code')}
          placeholder={t('global.code')}
          type="text"
          validate={required}
          disabled={disableEdit}
        />
        <IdentityField
          gridSpan={2}
          name="drafting_date"
          disabled={disableEdit}
          initialValue={contract.drafting_date}
          component={DatePickerAdapter}
          label={t('global.drafting_date')}
          type="date"
        />
        <IdentityField
          gridSpan={2}
          name="expiration_date"
          disabled={disableEdit}
          initialValue={contract.expiration_date}
          component={DatePickerAdapter}
          label={t('global.expiration')}
          placeholder={t('global.expiration')}
          type="date"
        />
        <IdentityField
          name="active"
          disabled={disableEdit}
          initialValue={contract.active}
          component={CheckboxAdapter}
          label={t('global.active')}
          placeholder={t('global.active')}
          type="checkbox"
        />
        <IdentityField
          gridSpan={6}
          name="tender_ref"
          disabled={disableEdit}
          initialValue={contract.tender_ref}
          component={TextFieldAdapter}
          label={t('global.tender')}
          type="text"
        />
        <IdentityField
          gridSpan={6}
          name="commission_ref"
          disabled={disableEdit}
          initialValue={contract.commission_ref}
          component={TextFieldAdapter}
          label={t('global.commission')}
          type="text"
        />
        <IdentityField
          gridSpan={12}
          name="order_email"
          disabled={disableEdit}
          initialValue={contract.order_email}
          component={TextFieldAdapter}
          label={t('global.order_email')}
          type="email"
        />
        <IdentityField
          gridSpan={12}
          name="note"
          disabled={disableEdit}
          initialValue={contract.note}
          component={TextFieldAdapter}
          label={t('global.notes')}
          type="text"
        />
        {suppliers?.length > 0 && (
          <IdentityField
            gridSpan={6}
            name="supplier"
            disabled={disableEdit}
            component={AutocompleteAdapter}
            initValue={
              contract.supplier
                ? {
                    id: contract.supplier.id,
                    code: `${contract.supplier.code} - ${contract.supplier['company_name']}`
                  }
                : null
            }
            // defaultValue={contract.supplier.id}
            options={suppliers}
            label={t('global.supplier')}
            type="text"
          />
        )}

        {laboratories?.length > 0 && (
          <IdentityField
            gridSpan={6}
            name="laboratories"
            component={MultipleAutocompleteAdapter}
            options={laboratories}
            initValue={contract.laboratories}
            disabled={disableEdit}
            label={t('global.laboratories')}
            type="text"
          />
        )}
        <IdentityField
          gridSpan={6}
          name="maintenance_max_compliance_days"
          label={t(`entities.contract.maintenance_max_compliance_days`)}
          initialValue={contract.maintenance_max_compliance_days}
          component={TextFieldAdapter}
          type="number"
          disabled={disableEdit}
        />
        <IdentityField
          gridSpan={6}
          name="ticketing_max_compliance_hours"
          label={t(`entities.contract.ticketing_max_compliance_hours`)}
          initialValue={contract.ticketing_max_compliance_hours}
          component={TextFieldAdapter}
          disabled={disableEdit}
          type="number"
        />
      </EnhancedForm>
    </div>
  );
}

export default ContractHeaderForm;
